import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../firebase';
import { getLocalCacheVersion } from '../../services/cacheInvalidation';
import { trackRead } from '../../services/firebaseUsageTracker';

type InvalidateCacheResult = {
  success: boolean;
  previousVersion: number;
  newVersion: number;
};

type CacheInfo = {
  serverVersion: number;
  lastInvalidated: Date | null;
  invalidatedBy: string | null;
};

export const CacheManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
  const [localVersion, setLocalVersion] = useState<number>(0);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Load current cache version info
  useEffect(() => {
    const loadCacheInfo = async () => {
      try {
        const configRef = doc(db, 'config', 'app');
        const configSnap = await getDoc(configRef);
        trackRead('config/app', 'admin-cache-info');

        if (configSnap.exists()) {
          const data = configSnap.data();
          setCacheInfo({
            serverVersion: data.cacheVersion ?? 0,
            lastInvalidated: data.lastInvalidated?.toDate() ?? null,
            invalidatedBy: data.invalidatedBy ?? null,
          });
        } else {
          setCacheInfo({
            serverVersion: 0,
            lastInvalidated: null,
            invalidatedBy: null,
          });
        }
      } catch (err) {
        console.warn('Failed to load cache info:', err);
      }
    };

    loadCacheInfo();
    setLocalVersion(getLocalCacheVersion());
  }, []);

  const handleInvalidateCache = async () => {
    const confirmed = window.confirm(
      'This will clear cached data for ALL users on their next visit.\n\n' +
      'Use this after pushing important updates that require fresh state.\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    setLoading(true);
    setResult(null);

    try {
      const invalidateCacheFn = httpsCallable<Record<string, never>, InvalidateCacheResult>(
        functions,
        'invalidateCache'
      );
      const response = await invalidateCacheFn({});
      const data = response.data;

      setCacheInfo(prev => ({
        ...prev!,
        serverVersion: data.newVersion,
        lastInvalidated: new Date(),
        invalidatedBy: 'you',
      }));

      setResult({
        success: true,
        message: `Cache invalidated! Version: ${data.previousVersion} → ${data.newVersion}`,
      });
    } catch (err) {
      const error = err as { message?: string };
      setResult({
        success: false,
        message: error.message || 'Failed to invalidate cache',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <h4 className="text-sm font-semibold text-gray-200">Remote Cache Invalidation</h4>
      </div>

      <p className="text-xs text-gray-400 mb-4">
        Force all users to clear their local cache on next visit. Use after deploying breaking changes
        or when users need fresh state.
      </p>

      {/* Cache version info */}
      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        <div className="bg-gray-900/50 rounded p-2">
          <div className="text-gray-500 mb-1">Server Version</div>
          <div className="text-white font-mono">{cacheInfo?.serverVersion ?? '—'}</div>
        </div>
        <div className="bg-gray-900/50 rounded p-2">
          <div className="text-gray-500 mb-1">Your Local Version</div>
          <div className="text-white font-mono">{localVersion}</div>
        </div>
        {cacheInfo?.lastInvalidated && (
          <div className="bg-gray-900/50 rounded p-2 col-span-2">
            <div className="text-gray-500 mb-1">Last Invalidated</div>
            <div className="text-white text-xs">
              {cacheInfo.lastInvalidated.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Result message */}
      {result && (
        <div
          className={`mb-4 p-3 rounded text-sm ${
            result.success
              ? 'bg-green-900/50 border border-green-700 text-green-300'
              : 'bg-red-900/50 border border-red-700 text-red-300'
          }`}
        >
          {result.message}
        </div>
      )}

      {/* Invalidate button */}
      <button
        onClick={handleInvalidateCache}
        disabled={loading}
        className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
          loading
            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-orange-600 hover:bg-orange-500 text-white'
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Invalidating...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Invalidate All User Caches
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 mt-2 text-center">
        Users will reset to defaults on their next app load
      </p>
    </div>
  );
};
